// src/ui/hooks/useForm.ts
import { useState } from "preact/hooks";
import type { StateUpdater } from "preact/hooks";

export interface FormField<T = string> {
    name: string;
    value: T;
    error?: string;
    validate?: (value: T) => string | undefined;
}

export interface UseFormConfig<T extends Record<string, any>> {
    initialValues: T;
    onSubmit: (values: T) => Promise<void>;
    validate?: (values: T) => Partial<Record<keyof T, string>>;
}

export interface UseFormReturn<T extends Record<string, any>> {
    values: T;
    errors: Partial<Record<keyof T, string>>;
    isSubmitting: boolean;
    handleChange: (
        name: keyof T,
    ) => (e: Event & { currentTarget: HTMLInputElement }) => void;
    handleSubmit: (e: Event) => Promise<void>;
    resetForm: () => void;
}

export function useForm<T extends Record<string, any>>({
    initialValues,
    onSubmit,
    validate,
}: UseFormConfig<T>): UseFormReturn<T> {
    const [values, setValues] = useState<T>(initialValues);
    const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange =
        (name: keyof T) => (e: Event & { currentTarget: HTMLInputElement }) => {
            const value = e.currentTarget.value;
            setValues((prev) => ({ ...prev, [name]: value }));
            // Clear error when field is modified
            if (errors[name]) {
                setErrors((prev) => ({ ...prev, [name]: undefined }));
            }
        };

    const handleSubmit = async (e: Event) => {
        e.preventDefault();

        if (validate) {
            const validationErrors = validate(values);
            if (Object.keys(validationErrors).length > 0) {
                setErrors(validationErrors);
                return;
            }
        }

        setIsSubmitting(true);
        try {
            await onSubmit(values);
        } catch (error) {
            console.error("Form submission failed:", error);
            setErrors((prev) => ({
                ...prev,
                submit:
                    error instanceof Error
                        ? error.message
                        : "Submission failed",
            }));
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setValues(initialValues);
        setErrors({});
    };

    return {
        values,
        errors,
        isSubmitting,
        handleChange,
        handleSubmit,
        resetForm,
    };
}
